/**
 * Release Timeline
 *
 * @module features/releases/components/ReleaseTimeline
 * @description Vertical timeline showing commits and release runs (or, for a
 * pushed-image application, its builds). One layout
 * for both single- and multi-repo configs: commits and runs interleaved
 * chronologically down a single continuous rail. When there are 2+ linked
 * repos, each commit gets a colored `{role}` mustache tag in front of the SHA
 * so the source repo is obvious at a glance, plus a small legend at the top.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Text } from '@chatsdet/ui';
import { CircleNotch } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { isPromotionRunbook, type PromotionRunbook, type Runbook } from '../../runbooks/types';
import type { TimelineCommitEntry as CommitEntry, TimelineEntry } from '../hooks/useReleaseTimeline';
import { isBuildEntry, isCommitEntry, isRunEntry } from '../hooks/useReleaseTimeline';
import { TimelineCommitEntry } from './TimelineCommitEntry';
import { TimelineRunEntry } from './TimelineRunEntry';
import { TimelineBuildEntry } from './TimelineBuildEntry';
import type { ClaimTarget } from './ReleaseRowMenu';
import { PromoteCommitDialog, type PromotableCommit } from './PromoteCommitDialog';
import type { ReleaseQAConfigRepoEntry } from '../releaseTypes';
import { buildReleasedCommitMap } from '../releaseRunSelection';
import type { BandRect } from './ReleaseRunPaneReveal';

type Props = {
  entries: TimelineEntry[];
  propertyId: string;
  deployedSha?: string | null;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * Linked repos for the property's release config. Used to render the
   * per-repo color legend and to color the `{role}` tag prefix on each
   * commit row when there are 2+ repos.
   */
  repos?: ReleaseQAConfigRepoEntry[];
  /** The run currently shown in the master-detail pane, or `null`. */
  selectedRunId?: string | null;
  /** Select a run in the master-detail pane. `rect` is the clicked band's own slice — see `ReleaseRunPaneReveal`. */
  onSelectRun: (runId: string, rect?: BandRect) => void;
  /** The application's sandbox environments, for the row menus' Claim items; `null` when not set up for Sandboxes v2. */
  claimTarget: ClaimTarget | null;
};

/**
 * Curated palette for the per-repo `{role}` tag. Index N wraps mod-length so
 * extra repos still get a distinct color. Each entry exposes Tailwind text +
 * dot classes so the legend dot and the tag share a single color source.
 */
const REPO_PALETTE = [
  { text: 'text-indigo-600 cofactor-dark:text-indigo-400', dot: 'bg-indigo-500' },
  { text: 'text-teal-600 cofactor-dark:text-teal-400',   dot: 'bg-teal-500' },
  { text: 'text-pink-600 cofactor-dark:text-pink-400',   dot: 'bg-pink-500' },
  { text: 'text-amber-600 cofactor-dark:text-amber-400',  dot: 'bg-amber-500' },
] as const;

export type RepoTagInfo = {
  /** Short label shown in the `{label}` tag — `role` if set, else the repo name. */
  label: string;
  /** Tailwind text color class for the tag. */
  textClass: string;
};

/** Build a lookup of repoFullName → tag info, color-indexed by displayOrder. */
function buildRepoTags(
  repos: ReleaseQAConfigRepoEntry[]
): Map<string, RepoTagInfo> {
  const map = new Map<string, RepoTagInfo>();
  repos.forEach((repo, index) => {
    const palette = REPO_PALETTE[index % REPO_PALETTE.length]!;
    map.set(repo.repoFullName, {
      label: repo.role || repo.repoName || repo.repoFullName,
      textClass: palette.text,
    });
  });
  return map;
}

export function ReleaseTimeline({
  entries,
  propertyId,
  deployedSha,
  hasMore,
  isLoadingMore,
  onLoadMore,
  repos,
  selectedRunId,
  onSelectRun,
  claimTarget,
}: Props) {
  const repoCount = repos?.length ?? 0;
  const showRepoTags = repoCount > 1;
  const repoTags = showRepoTags ? buildRepoTags(repos!) : null;
  const primaryRepoFullName = repos?.[0]?.repoFullName ?? null;

  // Same queryKey as PropertyCodeRunbooksSection's runbooks query so the two
  // views share one cache entry instead of firing duplicate fetches.
  const runbooksQuery = useQuery({
    queryKey: ['property-runbooks', propertyId],
    queryFn: async (): Promise<{ ok: boolean; items: Runbook[] }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/runbooks`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch runbooks');
      return res.json();
    },
  });
  const promotions = React.useMemo(
    () => (runbooksQuery.data?.items ?? []).filter(isPromotionRunbook),
    [runbooksQuery.data]
  );
  const promotionsByRepoId = React.useMemo(() => {
    const map = new Map<string, PromotionRunbook[]>();
    for (const p of promotions) {
      const list = map.get(p.propertyGithubRepoId) ?? [];
      list.push(p);
      map.set(p.propertyGithubRepoId, list);
    }
    return map;
  }, [promotions]);
  // Set of repos any promotion belongs to. Used to decide what a commit with
  // no repo id (see below) is allowed to see.
  const promotionRepoIds = React.useMemo(
    () => new Set(promotions.map((p) => p.propertyGithubRepoId)),
    [promotions]
  );

  /**
   * A commit must only ever be offered promotions from its OWN repo. The
   * server validates that a promoted sha is an ancestor of the target
   * repo's default branch — but it cannot refuse a sha that happens to be
   * valid on *both* repos in a multi-repo property, and an unscoped list
   * would let an operator ship a repo-A commit through repo-B's runbook in
   * that case (silently, since the sha check passes). So: scope strictly to
   * `entry.propertyGithubRepoId`, and when that's null (legacy rows,
   * pre-backfill) only fall back to the full list if every promotion in the
   * property already comes from a single repo — i.e. the null case is
   * unambiguous. If promotions span more than one repo and the commit isn't
   * attributed to one, we cannot tell which script it belongs to, so we
   * fail closed and hide Promote entirely rather than guess.
   */
  const promotionsForEntry = (entry: CommitEntry): PromotionRunbook[] => {
    if (entry.propertyGithubRepoId) {
      return promotionsByRepoId.get(entry.propertyGithubRepoId) ?? [];
    }
    return promotionRepoIds.size <= 1 ? promotions : [];
  };

  // A single dialog for the whole timeline — rows report the commit they want
  // to promote upward rather than each owning a (mostly-inert) dialog instance.
  const [promoting, setPromoting] = React.useState<{
    commit: PromotableCommit;
    promotions: PromotionRunbook[];
  } | null>(null);

  const tagFor = (repoFullName: string | null): RepoTagInfo | null => {
    if (!repoTags) return null;
    const key = repoFullName ?? primaryRepoFullName;
    return key ? (repoTags.get(key) ?? null) : null;
  };

  // Pair each run with the commit it actually tested up to, by SHA (see
  // `buildReleasedCommitMap`'s doc for why this can't just take the next
  // entry in list order).
  const releasedCommitByRunId = React.useMemo(() => buildReleasedCommitMap(entries), [entries]);
  const bandedCommitIds = React.useMemo(
    () => new Set(Array.from(releasedCommitByRunId.values(), (c) => c.id)),
    [releasedCommitByRunId]
  );

  return (
    <div className="relative">
      {showRepoTags && (
        <RepoLegend repos={repos!} />
      )}

      {/* The page's schematic grid is a 1px hairline on a 40px pitch. The rail
          was also 1px in a near-identical neutral, so wherever the two lined up
          it read as a doubled line. Fixed by making the rail deliberate — 2px
          and token-coloured — rather than by covering the background: the grid
          is the page's own texture and the commits are meant to sit on it. Only
          the release bands get a surface of their own. */}
      <div className="relative">
        {/* The rail's x-position is dictated by the release band, whose status
            node sits inside a bordered card and is therefore inset by that
            card's border (1px) plus its `padding="sm"` (1rem) — `calc(1rem+1px)`,
            14px+1px=15px. The node is 28px wide (`w-8`), so its center sits
            14px further in, at 29px. `left-8` (2rem=28px) to `left-8 + w-[2px]`
            (30px) straddles that center exactly. `TimelineCommitEntry` carries
            the same inset, so band nodes, commit nodes, and the rail share one
            vertical line. Verified by measuring all three in the browser. */}
        <div className="absolute left-8 top-0 bottom-0 w-[2px] rounded-full bg-border" />
        <div className="space-y-0">
          {entries.map((entry) => {
            if (isCommitEntry(entry)) {
              // Rendered inline in its run's band instead — skip the
              // duplicate standalone row.
              if (bandedCommitIds.has(entry.id)) return null;
              // Every commit row gets a ship affordance — Run tests (the
              // dialog's default, always-available action) needs no
              // promotion runbook at all, so this isn't gated on
              // `rowPromotions.length`. `promotionsForEntry` still scopes
              // which `Promote → <stage>` options (if any) the dialog
              // additionally offers.
              const rowPromotions = promotionsForEntry(entry);
              return (
                <TimelineCommitEntry
                  key={`commit-${entry.id}`}
                  entry={entry}
                  deployedSha={deployedSha}
                  repoTag={tagFor(entry.repoFullName)}
                  claimTarget={claimTarget}
                  onPromote={(commit) => setPromoting({ commit, promotions: rowPromotions })}
                />
              );
            }
            if (isRunEntry(entry)) {
              const releasedCommit = releasedCommitByRunId.get(entry.id) ?? null;
              const rowPromotions = releasedCommit ? promotionsForEntry(releasedCommit) : [];
              return (
                <TimelineRunEntry
                  key={`run-${entry.id}`}
                  entry={entry}
                  isSelected={selectedRunId === entry.id}
                  onSelect={onSelectRun}
                  releasedCommit={releasedCommit}
                  claimTarget={claimTarget}
                  deployedSha={deployedSha}
                  repoTag={releasedCommit ? tagFor(releasedCommit.repoFullName) : null}
                  onPromoteReleasedCommit={
                    releasedCommit
                      ? (commit) => setPromoting({ commit, promotions: rowPromotions })
                      : undefined
                  }
                />
              );
            }
            if (isBuildEntry(entry)) {
              return (
                <TimelineBuildEntry
                  key={`build-${entry.id}`}
                  entry={entry}
                  isSelected={selectedRunId === entry.id}
                  onSelect={onSelectRun}
                  claimTarget={claimTarget}
                />
              );
            }
            return null;
          })}
        </div>

        {hasMore && (
          // Aligned with the content column, not the rail: inset (1rem+1px)
          // + node width (2rem) + row gap (0.75rem) = 3.75rem+1px.
          <div className="relative pl-[calc(3.75rem+1px)] pt-4">
            <Button
              variant="secondary"
              size="sm"
              onPress={onLoadMore}
              isDisabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <CircleNotch className="animate-spin mr-2" size={16} />
                  Loading...
                </>
              ) : (
                'Load more'
              )}
            </Button>
          </div>
        )}
      </div>

      <PromoteCommitDialog
        commit={promoting?.commit ?? null}
        promotions={promoting?.promotions ?? []}
        propertyId={propertyId}
        onOpenChange={(open) => {
          if (!open) setPromoting(null);
        }}
      />
    </div>
  );
}

function RepoLegend({ repos }: { repos: ReleaseQAConfigRepoEntry[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 pb-3 border-b border-border">
      <Text size="xs" className="text-muted-foreground uppercase tracking-wide font-medium">
        Repos
      </Text>
      {repos.map((repo, index) => {
        const palette = REPO_PALETTE[index % REPO_PALETTE.length]!;
        const label = repo.role || repo.repoName || repo.repoFullName;
        return (
          <div key={repo.linkId} className="flex items-center gap-1.5 min-w-0">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${palette.dot}`} />
            <Text size="xs" weight="medium" className={`font-mono ${palette.text}`}>
              {`{${label}}`}
            </Text>
            <Text size="xs" className="text-muted-foreground truncate">
              {repo.repoFullName}
            </Text>
            <Text size="xs" className="text-muted-foreground/70 truncate">
              {repo.releaseBranch ?? repo.repoDefaultBranch ?? 'main'}
            </Text>
          </div>
        );
      })}
    </div>
  );
}
